import { Component, OnInit, inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import {
    ACCOUNT_ID_FIELDS, AccountIdFieldInfo, ApplicationResubmitReason, ApplicationResubmitRequestPayload,
    ApplicationStatus, TerpecaUser
} from 'src/app/models/user.model';
import { AuthService } from 'src/app/services/auth.service';
import { buildResubmitEmailHtml, getResubmitEmailSubject } from 'src/app/utils/resubmit-email.utils';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-requestresubmitdialog',
  standalone: false,
  templateUrl: './requestresubmitdialog.component.html',
  styleUrl: './requestresubmitdialog.component.css'
})
export class RequestResubmitDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<RequestResubmitDialogComponent>>(MatDialogRef);
  private auth = inject(AuthService);
  private sanitizer = inject(DomSanitizer);
  data = inject(MAT_DIALOG_DATA);

  form: UntypedFormGroup;
  visibleIdFields: (AccountIdFieldInfo & { value: string })[] = [];

  ngOnInit(): void {
    const user: TerpecaUser = this.data.applicant.user;
    const filledIdFields = ACCOUNT_ID_FIELDS
        .map(info => ({ ...info, value: String(user[info.field] || '').trim() }))
        .filter(info => info.value);
    this.visibleIdFields = filledIdFields.length ? filledIdFields : ACCOUNT_ID_FIELDS.map(info => ({
      ...info,
      value: ''
    }));

    const invalidIdControls: { [key: string]: UntypedFormControl } = {};
    for (const info of this.visibleIdFields) {
      invalidIdControls[info.field] = new UntypedFormControl(false);
    }

    this.form = new UntypedFormGroup({
      onePersonOnly: new UntypedFormControl(false),
      insufficientIdentityInfo: new UntypedFormControl(false),
      insufficientGameCount: new UntypedFormControl(false),
      insufficientExperienceInfo: new UntypedFormControl(false),
      insufficientConflictDisclosure: new UntypedFormControl(false),
      affiliationsNotInBio: new UntypedFormControl(false),
      invalidName: new UntypedFormControl(false),
      invalidCity: new UntypedFormControl(false),
      invalidIds: new UntypedFormControl(false),
      invalidIdFields: new UntypedFormGroup(invalidIdControls),
      insufficientBio: new UntypedFormControl(false),
      custom: new UntypedFormControl(false),
      customInstructions: new UntypedFormControl(''),
      ccMe: new UntypedFormControl(true)
    });
  }

  get selectedInvalidIdFields(): string[] {
    const values = this.form?.value?.invalidIdFields || {};
    return this.visibleIdFields.map(info => info.field).filter(field => values[field]);
  }

  get invalidIdsNeedSelection(): boolean {
    return Boolean(this.form?.value?.invalidIds) && this.selectedInvalidIdFields.length === 0;
  }

  get isUpgrade(): boolean {
    return Boolean(this.data.applicant.user.upgradeRequested);
  }

  get emailSubject(): string {
    return getResubmitEmailSubject(environment.projectTitle, this.isUpgrade);
  }

  get emailPreview(): SafeHtml {
    const payload = this.getPayload();
    if (!payload.reasons.length || this.invalidIdsNeedSelection) {
      const message = this.invalidIdsNeedSelection ?
          'Select which ID(s) are invalid to preview the email.' :
          'Select at least one reason to preview the email.';
      return this.sanitizer.bypassSecurityTrustHtml(`<p><i>${message}</i></p>`);
    }
    const user: TerpecaUser = this.data.applicant.user;
    return this.sanitizer.bypassSecurityTrustHtml(buildResubmitEmailHtml(payload, {
      userName: user.realName || user.displayName || '',
      isUpgrade: this.isUpgrade,
      innerOnly: true
    }));
  }

  onInvalidIdsChange() {
    if (!this.form.value.invalidIds) {
      const group = <UntypedFormGroup>this.form.get('invalidIdFields');
      for (const key of Object.keys(group.controls)) {
        group.get(key)?.setValue(false);
      }
    }
  }

  onInvalidIdFieldChange() {
    if (this.selectedInvalidIdFields.length) {
      this.form.get('invalidIds')?.setValue(true);
    }
  }

  getPayload(): ApplicationResubmitRequestPayload {
    if (!this.form) {
      return {
        status: ApplicationStatus.NONE,
        reasons: []
      };
    }
    const reasons: ApplicationResubmitReason[] = [];
    if (this.form.value.onePersonOnly) {
      reasons.push(ApplicationResubmitReason.ONE_PERSON_ONLY);
    }
    if (this.form.value.insufficientIdentityInfo) {
      reasons.push(ApplicationResubmitReason.INSUFFICIENT_IDENTITY_INFO);
    }
    if (this.form.value.insufficientGameCount) {
      reasons.push(ApplicationResubmitReason.INSUFFICIENT_GAME_COUNT);
    }
    if (this.form.value.insufficientExperienceInfo) {
      reasons.push(ApplicationResubmitReason.INSUFFICIENT_EXPERIENCE_INFO);
    }
    if (this.form.value.insufficientConflictDisclosure) {
      reasons.push(ApplicationResubmitReason.INSUFFICIENT_CONFLICT_DISCLOSURE);
    }
    if (this.form.value.affiliationsNotInBio) {
      reasons.push(ApplicationResubmitReason.AFFILIATIONS_NOT_IN_BIO);
    }
    if (this.form.value.invalidName) {
      reasons.push(ApplicationResubmitReason.INVALID_NAME);
    }
    if (this.form.value.invalidCity) {
      reasons.push(ApplicationResubmitReason.INVALID_CITY);
    }
    const invalidIdFields = this.selectedInvalidIdFields;
    if (this.form.value.invalidIds && invalidIdFields.length) {
      reasons.push(ApplicationResubmitReason.INVALID_IDS);
    }
    if (this.form.value.insufficientBio) {
      reasons.push(ApplicationResubmitReason.INSUFFICIENT_BIO);
    }
    if (this.form.value.custom && this.form.value.customInstructions) {
      reasons.push(ApplicationResubmitReason.CUSTOM);
    }
    const payload: ApplicationResubmitRequestPayload = {
      status: this.isUpgrade ? this.data.applicant.user.status : ApplicationStatus.NONE,
      reasons
    };
    if (this.form.value.custom && this.form.value.customInstructions) {
      payload.customInstructions = this.form.value.customInstructions;
    }
    if (this.form.value.invalidIds && invalidIdFields.length) {
      payload.invalidIdFields = invalidIdFields;
    }
    if (this.form.value.ccMe) {
      payload.ccEmail = this.auth.currentUser.appEmail || this.auth.currentUser.email;
    }
    return payload;
  }

  formIsValid(): boolean {
    if (this.invalidIdsNeedSelection) {
      return false;
    }
    return this.getPayload().reasons.length > 0;
  }

  cancel() {
    this.dialogRef.close(false);
  }

  async submit() {
    if (this.isUpgrade) {
      await this.data.applicant.auth.denyUpgrade(this.data.applicant.user.uid, this.getPayload());
    } else {
      await this.data.applicant.auth.setApplicationStatus(this.data.applicant.user.uid, this.getPayload());
    }
    this.dialogRef.close(true);
    this.data.applicant.resubmitRequested();
  }
}
